/*
 This file is part of GNU Taler
 (C) 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU Affero General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU Affero General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>

 SPDX-License-Identifier: AGPL-3.0-or-later
 */

import {
  Codec,
  buildCodecForObject,
  buildCodecForUnion,
  codecForBoolean,
  codecForConstString,
  codecForEither,
  codecForList,
  codecForString,
  codecForStringURL,
  codecOptional,
} from "./codec.js";
import { TalerPreparedTransferApi } from "./index.js";
import { codecForAmountString } from "./amounts.js";
import { codecForTimestamp } from "./time.js";
import {
  AmountString,
  EddsaPublicKey,
  EddsaSignature,
  Timestamp,
  codecForEddsaPublicKey,
  codecForEddsaSignature,
} from "./types-taler-common.js";

export type SubjectFormat = "SIMPLE" | "URI" | "CH_QR_BILL";

export interface PreparedTransferConfig {
  // Name of the API.
  name: "taler-prepared-transfer";

  // libtool-style representation of the protocol version, see
  // https://www.gnu.org/software/libtool/manual/html_node/Versioning.html#Versioning
  // The format is "current:revision:age".
  version: string;

  // Currency used by this API.
  currency: string;

  // URN of the implementation (needed to interpret 'revision' in version).
  // @since v0, may become mandatory in the future.
  implementation?: string;

  // Supported formats for registration, there must at least one.
  supported_formats: SubjectFormat[];
}

export interface RegistrationRequest {
  // Amount to transfer
  credit_amount: AmountString;

  // Transfer types
  type: "reserve" | "kyc";

  // Public key algorithm
  alg: "EdDSA";

  // Account public key for the exchange
  account_pub: EddsaPublicKey;

  // Public key encoded inside the subject
  authorization_pub: EddsaPublicKey;

  // Signature of the account_pub key using the authorization_pub private key
  authorization_sig: EddsaSignature;

  // Whether the authorization_pub will be reused for recurrent transfers
  // Disable bounces in case of authorization_pub reuse
  recurrent: boolean;
}

// Union discriminated by the "type" field.
export type TransferSubject = SimpleSubject | UriSubject | SwissQrBillSubject;

export interface SimpleSubject {
  // Subject for system accepting large subjects
  type: "SIMPLE";

  // Amount to transfer
  credit_amount: AmountString;

  // Encoded string containing either the full key and transfer type or a
  // derived short subject
  subject: string;
}

export interface UriSubject {
  // Subject for system accepting prepared payments
  type: "URI";

  // Amount to transfer
  credit_amount: AmountString;

  // Prepared payments confirmation URI
  uri: string;
}

export interface SwissQrBillSubject {
  // Subject for Swiss QR Bill
  type: "CH_QR_BILL";

  // Amount to transfer
  credit_amount: AmountString;

  // 27-digit QR Reference number
  qr_reference_number: string;
}

export interface RegistrationResponse {
  // The transfer subject encoded in all supported formats
  subjects: TransferSubject[];

  // Expiration date after which this subject is expected to be reused
  expiration: Timestamp;
}

export interface Unregistration {
  // Current timestamp in the ISO 8601
  timestamp: string;

  // Public key used for registration
  authorization_pub: EddsaPublicKey;

  // Signature of the timestamp using the authorization_pub private key
  // Prevent replay attack
  authorization_sig: EddsaSignature;
}

export const codeForSubjectFormat =
  (): Codec<TalerPreparedTransferApi.SubjectFormat> =>
    codecForEither(
      codecForConstString("SIMPLE"),
      codecForConstString("URI"),
      codecForConstString("CH_QR_BILL"),
    );

export const codecForPreparedTransferConfig =
  (): Codec<TalerPreparedTransferApi.PreparedTransferConfig> =>
    buildCodecForObject<TalerPreparedTransferApi.PreparedTransferConfig>()
      .property("currency", codecForString())
      .property("implementation", codecOptional(codecForString()))
      .property("name", codecForConstString("taler-prepared-transfer"))
      .property("supported_formats", codecForList(codeForSubjectFormat()))
      .property("version", codecForString())
      .build("TalerPreparedTransferApi.PreparedTransferConfig");

export const codecForRegistrationRequest =
  (): Codec<TalerPreparedTransferApi.RegistrationRequest> =>
    buildCodecForObject<TalerPreparedTransferApi.RegistrationRequest>()
      .property("credit_amount", codecForAmountString())
      .property(
        "type",
        codecForEither(
          codecForConstString("reserve"),
          codecForConstString("kyc"),
        ),
      )
      .property("alg", codecForEither(codecForConstString("EdDSA")))
      .property("account_pub", codecForEddsaPublicKey())
      .property("authorization_pub", codecForEddsaPublicKey())
      .property("authorization_sig", codecForEddsaSignature())
      .property("recurrent", codecForBoolean())
      .build("TalerWireGatewayApi.RegistrationRequest");

export const codecForTransferSubject =
  (): Codec<TalerPreparedTransferApi.TransferSubject> =>
    buildCodecForUnion<TalerPreparedTransferApi.TransferSubject>()
      .discriminateOn("type")
      .alternative("SIMPLE", codecForSimpleSubject())
      .alternative("URI", codecForUriSubject())
      .alternative("CH_QR_BILL", codecForSwissQrBillSubject())
      .build("TalerPreparedTransferApi.TransferSubject");

export const codecForSimpleSubject =
  (): Codec<TalerPreparedTransferApi.SimpleSubject> =>
    buildCodecForObject<TalerPreparedTransferApi.SimpleSubject>()
      .property("type", codecForConstString("SIMPLE"))
      .property("credit_amount", codecForAmountString())
      .property("subject", codecForString())
      .build("TalerPreparedTransferApi.SimpleSubject");

export const codecForUriSubject =
  (): Codec<TalerPreparedTransferApi.UriSubject> =>
    buildCodecForObject<TalerPreparedTransferApi.UriSubject>()
      .property("type", codecForConstString("URI"))
      .property("credit_amount", codecForAmountString())
      .property("uri", codecForStringURL())
      .build("TalerPreparedTransferApi.UriSubject");

export const codecForSwissQrBillSubject =
  (): Codec<TalerPreparedTransferApi.SwissQrBillSubject> =>
    buildCodecForObject<TalerPreparedTransferApi.SwissQrBillSubject>()
      .property("type", codecForConstString("CH_QR_BILL"))
      .property("credit_amount", codecForAmountString())
      .property("qr_reference_number", codecForString())
      .build("TalerPreparedTransferApi.SwissQrBillSubject");

export const codecForRegistrationResponse =
  (): Codec<TalerPreparedTransferApi.RegistrationResponse> =>
    buildCodecForObject<TalerPreparedTransferApi.RegistrationResponse>()
      .property("subjects", codecForList(codecForTransferSubject()))
      .property("expiration", codecForTimestamp)
      .build("TalerWireGatewayApi.RegistrationResponse");

export const codecForUnregistration =
  (): Codec<TalerPreparedTransferApi.Unregistration> =>
    buildCodecForObject<TalerPreparedTransferApi.Unregistration>()
      .property("timestamp", codecForString())
      .property("authorization_pub", codecForEddsaPublicKey())
      .property("authorization_sig", codecForEddsaSignature())
      .build("TalerPreparedTransferApi.Unregistration");
